import { TypedDocumentNode } from "@graphql-typed-document-node/core/typings";
import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  SearchOrganisationsRequest,
  SearchOrganisationsRequest_AnchorOrganisation as AnchorOrganisation,
  SearchOrganisationsViaOsQuery,
} from "lib/generated/app-service-gql/graphql";
import { OrgSortBy, OrgSortOrder, SearchOrgRole } from "lib/types/graphQLEnums";
import { useGraphQlInfiniteQuery, useGraphQlQuery } from "../useGraphQlClient";

/**
 * Which optional fields to ask the server for.
 *
 * Everything gated here is a per-org `@ResolveField` on the backend, so each flag costs another
 * lookup for every org in the page — at the 500-row page sizes the org-merge screens use that is
 * the difference between two round trips per org and five. `contactDetails` is the exception: those
 * come straight off the org document, so it only trims payload size.
 */
export type SearchOrganisationsInclude = {
  noticeCount?: boolean;
  awardCount?: boolean;
  aliases?: boolean;
  buyerLists?: boolean;
  isSme?: boolean;
  oscarTown?: boolean;
  categoryAssignments?: boolean;
  contactDetails?: boolean;
};

/**
 * Filters that pass straight through take their nullability from the schema, so a caller holding
 * request-shaped filter state can hand them over untouched. The rest are declared here because this
 * hook renames or defaults them.
 */
export type SearchOrganisationsParams = Pick<
  SearchOrganisationsRequest,
  | "ids"
  | "names"
  | "listIds"
  | "regionIds"
  | "countryCodes"
  | "isSme"
  | "limit"
  | "page"
  | "includeCustomOrganisations"
> & {
  pubSecOrgTypes?: string[];
  sortBy?: OrgSortBy;
  sortOrder?: OrgSortOrder;
  anchorOrganisation?: AnchorOrganisation;
  role: SearchOrgRole;
  include?: SearchOrganisationsInclude;
};

type SearchOrganisationsOptions = UseQueryOptions<
  SearchOrganisationsViaOsQuery,
  unknown,
  SearchOrganisationsViaOsQuery,
  QueryKey
>;

type OrganisationsQueryVariables = {
  request: SearchOrganisationsRequest;
  includeNoticeCount: boolean;
  includeAwardCount: boolean;
  includeAliases: boolean;
  includeBuyerLists: boolean;
  includeIsSme: boolean;
  includeOscarTown: boolean;
  includeCategoryAssignments: boolean;
  includeContactDetails: boolean;
};

const organisationsQuery: TypedDocumentNode<
  SearchOrganisationsViaOsQuery,
  OrganisationsQueryVariables
> = graphql(`
  query searchOrganisationsViaOs(
    $request: SearchOrganisationsRequest!
    $includeNoticeCount: Boolean!
    $includeAwardCount: Boolean!
    $includeAliases: Boolean!
    $includeBuyerLists: Boolean!
    $includeIsSme: Boolean!
    $includeOscarTown: Boolean!
    $includeCategoryAssignments: Boolean!
    $includeContactDetails: Boolean!
  ) {
    searchOrganisations(SearchOrganisationsRequest: $request) {
      orgs {
        id
        name
        isCustom
        primaryRole
        isSme @include(if: $includeIsSme)
        noticeCount @include(if: $includeNoticeCount)
        awardCount @include(if: $includeAwardCount)
        aliases @include(if: $includeAliases)
        buyerLists @include(if: $includeBuyerLists) {
          id
          name
        }
        address
        town
        countryCode
        categoryAssignments @include(if: $includeCategoryAssignments) {
          categoryId
          categoryName
          source
        }
        oscarTown @include(if: $includeOscarTown)
        isStotlesVerified
        url @include(if: $includeContactDetails)
        email @include(if: $includeContactDetails)
        phone @include(if: $includeContactDetails)
        postalCode @include(if: $includeContactDetails)
      }
      total
    }
  }
`);

export type OrganisationData = SearchOrganisationsViaOsQuery["searchOrganisations"]["orgs"][number];

export type OrganisationCategoryData = NonNullable<
  SearchOrganisationsViaOsQuery["searchOrganisations"]["orgs"][number]["categoryAssignments"]
>[number];

// The gated fields other than `contactDetails` were always selected before they were gated, so they
// default on: a caller opts out of cost rather than opting in to data it already had.
function buildIncludeVariables(
  include: SearchOrganisationsInclude | undefined,
): Omit<OrganisationsQueryVariables, "request"> {
  return {
    includeNoticeCount: include?.noticeCount ?? true,
    includeAwardCount: include?.awardCount ?? true,
    includeAliases: include?.aliases ?? true,
    includeBuyerLists: include?.buyerLists ?? false,
    includeIsSme: include?.isSme ?? true,
    includeOscarTown: include?.oscarTown ?? true,
    includeCategoryAssignments: include?.categoryAssignments ?? true,
    includeContactDetails: include?.contactDetails ?? false,
  };
}

export function useOrganisations(
  {
    sortBy,
    sortOrder,
    pubSecOrgTypes,
    include,
    includeCustomOrganisations,
    ...rest
  }: SearchOrganisationsParams,
  options?: SearchOrganisationsOptions,
) {
  const searchOrgsRequest: SearchOrganisationsRequest = {
    ...rest,
    sortBy: sortBy ?? OrgSortBy.NoticeCount,
    sortOrder: sortOrder ?? OrgSortOrder.Desc,
    categoriesPublicSectorOrgType: pubSecOrgTypes,
    includeCustomOrganisations,
  };

  const includeVariables = buildIncludeVariables(include);

  const data = useGraphQlQuery<SearchOrganisationsViaOsQuery, OrganisationsQueryVariables>(
    ["searchOrganisationsViaOs", searchOrgsRequest, includeVariables],
    organisationsQuery,
    [{ request: searchOrgsRequest, ...includeVariables }],
    {
      ...options,
    },
  );

  return data;
}

/** Helper which returns just one org.
 *
 * TODO: instead of doing this in the front end, add a new query in the resolver to only return one org!
 * TODO: this will give us better validation, error messages etc...
 */
export function useOrganisation(
  params: Omit<SearchOrganisationsParams, "page" | "limit">,
  options?: SearchOrganisationsOptions,
) {
  const { data, ...rest } = useOrganisations({ ...params, limit: 1, page: 1 }, options);

  return { data: data?.searchOrganisations.orgs[0], ...rest };
}

export function useInfiniteOrganisations({
  sortBy,
  sortOrder,
  pubSecOrgTypes,
  include,
  ...rest
}: SearchOrganisationsParams) {
  const searchOrgsRequest: SearchOrganisationsRequest = {
    ...rest,
    sortBy: sortBy ?? OrgSortBy.NoticeCount,
    sortOrder: sortOrder ?? OrgSortOrder.Desc,
    categoriesPublicSectorOrgType: pubSecOrgTypes,
  };

  const includeVariables = { ...buildIncludeVariables(include), includeBuyerLists: false };

  return useGraphQlInfiniteQuery<SearchOrganisationsViaOsQuery, OrganisationsQueryVariables>(
    ["organisations", searchOrgsRequest, includeVariables],
    organisationsQuery,
    (page) => [{ request: { ...searchOrgsRequest, page }, ...includeVariables }],
    {
      getNextPageParam: (lastPage, allPages) => {
        const totalOrgs = lastPage.searchOrganisations.total;
        const totalLoadedOrgs = allPages.reduce(
          (acc, page) => acc + page.searchOrganisations.orgs.length,
          0,
        );
        return totalOrgs > totalLoadedOrgs ? allPages.length + 1 : undefined;
      },
      getPreviousPageParam: (_, allPages) => allPages.length - 1 || 1,
    },
  );
}
